import {createTheme} from '@mui/material/styles';
import {red} from '@mui/material/colors';
import {outlinedInputClasses} from '@mui/material/OutlinedInput';

const sharedTypography = {
    fontFamily: 'CoreSansA45Regular, Arial, sans-serif', // Define your global font family
    h1: {
        fontFamily: 'CoreSansA45Regular',
        fontSize: '36px',
        margin: '20px 0',
        ['@media (max-width:760px)']: {
            fontSize: '24px',
        },
    },
    h2: {
        fontFamily: 'CoreSansA75ExtraBold',
        fontSize: '54px',
        margin: '20px 0',
        letterSpacing: '-2.7px',
        ['@media (max-width:760px)']: {
            fontSize: '38px',
        },
    },
    h3: {
        fontFamily: 'CoreSansA75ExtraBold',
        fontSize: '40px',
        // color: '#5c5c5c',
        fontWeight: 400,
        margin: '20px 0',
        ['@media (max-width:760px)']: {
            fontSize: '28px',
            lineHeight: '34px',
            color: '#303030',
            textAlign: 'center',
        },
    },
    h4: {
        fontFamily: 'CoreSansA55Medium',
        fontSize: '30px',
        margin: '20px 0',
        ['@media (max-width:760px)']: {
            fontSize: '28px',
        },
    },
    h5: {
        fontFamily: 'CoreSansA55Medium',
        fontSize: '20px',
        margin: '20px 0',
        ['@media (max-width:760px)']: {
            fontSize: '20px',
        },
    },
    h6: {
        fontFamily: 'GalanoGrotesque-SemiBold',
        fontSize: '18px',
        margin: '20px 0',
        ['@media (max-width:760px)']: {
            fontSize: '16px',
        },
    },
    body1: {
        fontFamily: 'CoreSansA45Regular',
        fontSize: '16px',
        lineHeight: '1.7',
        color: '#000000',
        ['@media (max-width:900px)']: {
            fontFamily: 'CoreSansA45Regular',
            fontSize: '17.5px',
            lineHeight: '1.8',
        },
    },
    body2: {
        fontFamily: 'CoreSansA75ExtraBold, Arial, sans-serif',
        fontSize: '14px',
        fontWeight: 400,
    },
}

const sharedComponents = {
    MuiTextField: {
        styleOverrides: {
            root: {
                shrink: false,
                '& legend': {display: 'none'},
                '& .MuiInputLabel-shrink': {opacity: 0, transition: "all 0.2s ease-in"},
                '& label.Mui-focused': {
                    color: 'var(--TextField-brandBorderFocusedColor)',
                },
            },
        },
    },
    MuiSelect: {
        styleOverrides: {
            root: {
                shrink: false,
            }
        }
    },
    MuiOutlinedInput: {
        styleOverrides: {
            notchedOutline: {
                borderColor: 'var(--TextField-brandBorderColor)',
            },
            root: {
                shrink: false,
                [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                    borderColor: 'var(--TextField-brandBorderHoverColor)',
                },
                [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                    borderColor: 'var(--TextField-brandBorderFocusedColor)',
                },
            },
        },
    },
    MuiIcon: {
        styleOverrides: {
            root: {
                fontSize: 'large',
                color: 'primary',
                '&.colorPrimary': {
                    fill: 'red',
                },
            },
        },
    },
    MuiFilledInput: {
        styleOverrides: {
            root: {
                shrink: false,
                '&::before, &::after': {
                    borderBottom: '2px solid var(--TextField-brandBorderColor)',
                },
                '&:hover:not(.Mui-disabled, .Mui-error):before': {
                    borderBottom: '2px solid var(--TextField-brandBorderHoverColor)',
                },
                '&.Mui-focused:after': {
                    borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)',
                },
            },
        },
    },
    MuiInput: {
        styleOverrides: {
            root: {
                shrink: false,
                '&::before': {
                    borderBottom: '2px solid var(--TextField-brandBorderColor)',
                },
                '&:hover:not(.Mui-disabled, .Mui-error):before': {
                    borderBottom: '2px solid var(--TextField-brandBorderHoverColor)',
                },
                '&.Mui-focused:after': {
                    borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)',
                },
            },
        },
    },
    MuiButton: {
        styleOverrides: {
            root: {
                disableElevation: true,
                '& .MuiButton-contained': {
                    disableElevation: true,
                },
                // color: whiteSecondary,
                // backgroundColor: '#fff',
                // '&:hover': {
                //     backgroundColor: lightSecondary,
                // },
                fontFamily: 'CoreSansA65Bold'
            },
        },
    },
}

const sharedHeadingStyles = {
    color: '#171717', // Shared color for h1, h2, and h3
};

export const darkTheme = createTheme({});

// A custom theme for this app
export const lightTheme = createTheme({
    palette: {
        mode: 'light',
        background: {
            paper: '#f1f1f1',
            default: '#fff',
        },
        text: {
            primary: '#373737',
        },
        primary: {
            main: '#1e1e1e',
            dark: '#000000',
            contrastText: '#E0E3E7',
        },
        secondary: {
            main: '#e89805',
            dark: '#b37200',
        },
        error: {
            main: red.A400,
        },
    },
    components: {
        ...sharedComponents,
        MuiTextField: {
            styleOverrides: {
                root: {
                    '--TextField-brandBorderColor': '#E0E3E7',
                    '--TextField-brandBorderHoverColor': '#B2BAC2',
                    '--TextField-brandBorderFocusedColor': '#004280',
                },
            },
        },
    },
    typography: {
        ...sharedTypography,
        h1: {
            ...sharedTypography.h1,
            ...sharedHeadingStyles,
        },
        h2: {
            ...sharedTypography.h2,
            ...sharedHeadingStyles,
        },
        h3: {
            ...sharedTypography.h3,
            ...sharedHeadingStyles,
        },
        h4: {
            ...sharedTypography.h4,
            color: '#272727',
        },
        allVariants: {
            color: '#252525'
        }
    },
    custom: {
        headerActiveItem: '#176c9a',
        activeHeaderNavLink:{
            color:'#ffffff',
            textDecoration: 'underline'
        },
        headerActiveItemGradient: {
            start: 'transparent',
            end: 'transparent',
            direction: 'to bottom',
        },
        headerBackgroundGradient: {
            start: '#272727',
            end: '#272727',
            direction: 'to bottom',
        },
        headerMobileNav: {
            backdropFilter: `blur(3px)`,
            backgroundColor: 'rgba(56, 60, 66, .66)',

        },
        headerMobileDrawerContent: {
            backgroundColor: '#fff',
            color: '#222',
            '& div .active': {
                backgroundColor: '#272727',
                background: 'none',
            },
            '& a': {
                fontFamily: 'CoreSansA55Medium',
                fontSize: '14px',
                color: '#005dba',
            },
            '& svg': {
                color: '#0073ba',
            },
            '& li': {
                stroke: '#02387c',
                borderBottom: '#efefef',
            },
            '& section ul li a, & h4 a': {
                color: '#0380cd' //mobile
            }

        },
        headerDesktopDrawerContent: {
            backgroundColor: '#fafafa',
            color: '#202020',
            '& div .active': {
                backgroundColor: '#002863',
                background: 'none',
            },
            '& a': {
                fontFamily: 'CoreSansA55Medium',
                fontSize: '14px',
                color: '#686868',
            },
            '& svg': {
                color: '#fff',
            },
            '& li': {
                stroke: '#02387c',
                borderBottom: '#404040'
            }
        },
        headerSubMegaNav: {
            borderBottom: `1px solid #002863`,
            backdropFilter: `blur(3px)`,
            backgroundColor: 'rgba(241,241,241,0.94)',
            '& a': {
                fontFamily: 'CoreSansA55Medium',
                fontSize: '14px',
                color: '#2b2b2b', // navitems here
                display: 'block',
            },
            '& ul li a ': {
                paddingBottom: '1px',
                paddingLeft: '2px',
                '&:hover': {
                    textDecoration: 'underline'
                }
            },
            '& ul li a::before ': {
                fontSize: '14px',
            },
            '& h4>a': {
                fontSize: '16px ',
                textDecoration: 'underline'
            }
        },
        headerNavItemColor: '#8c8c8c',
        headerActiveNavItemColor: '#010e75',
        headerNavLinks: {
            fontFamily: 'CoreSansA55Medium',
            fontSize: '12px',
        },
        footer: {
            titleColor: "#ffffff",
            linkColor: '#efefef',
            linkHoverColor: '#ffffff',
            iconColor: '#ffffff',
            socialFooterBackgroundColor: 'rgba(255,255,255,0.09)',
        },
        footerBackgroundGradient: {
            start: '#4c4c4c',
            end: '#202020',
            direction: 'to bottom',
        },
        brandedBackgroundBlock: {
            start: '#fff',
            end: '#c8e7ff',
            direction: 'to bottom',
            color: '#151515', //here?
            // background:'#0c3a8a'
        },
        brandedAlternateBackgroundBlock: {
            start: '#fff',
            end: '#e0f0ff',
            direction: 'to bottom',
            color: '#303030', //alternative h3
            // background:'#dee3f1'
        },
        footerBottomNav: '#000000',
    }
});

